import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import AppToasts from '@/components/AppToasts';
import { colors } from '@/constants/colors';
import useAuthStore from '@/store/authStore';
import {
   verifyTransferHandoffApi,
   signupFromTransferApi,
   acceptTransferApi,
   type TransferHandoffData,
} from '@/api/vendo';
import GoogleAuthButton from '@/components/Frontend/GoogleAuthButton';

/**
 * /transfers/handoff?token=... — public landing for buyers clicking the
 * device-transfer email link. Three states:
 *
 *   - Logged in + email matches  → auto-redirect to the existing review
 *     modal on /wifi-devices.
 *   - Logged in + wrong account  → "sign out and use email X" prompt.
 *   - Not logged in              → show signup form (pre-filled, locked)
 *     or "sign in" CTA depending on whether an account already exists
 *     for this email (the verify endpoint returns hasExistingAccount).
 *
 * The token is verified once on mount and again on signup submit — no
 * value is trusted from the URL beyond what the JWT signs.
 */

type Phase =
   | 'verifying'
   | 'error'
   | 'mismatched-account'
   | 'has-account-signin'
   | 'new-account-signup';

const TransferHandoff: React.FC = () => {
   const [params] = useSearchParams();
   const navigate = useNavigate();
   const token = params.get('token') || '';
   const { user, isAuthenticated, setAuth } = useAuthStore();

   const [phase, setPhase] = useState<Phase>('verifying');
   const [errMsg, setErrMsg] = useState<string>('');
   const [data, setData] = useState<TransferHandoffData | null>(null);
   const [name, setName] = useState('');
   const [password, setPassword] = useState('');
   const [confirmPassword, setConfirmPassword] = useState('');
   const [submitting, setSubmitting] = useState(false);
   const [showManualSignup, setShowManualSignup] = useState(false);
   const [acceptingAfterGoogle, setAcceptingAfterGoogle] = useState(false);

   // One-shot verification per token. Deliberately does NOT depend on
   // auth state — re-verifying on every login/logout burned through the
   // server's rate limit (429s) during real testing where the user
   // signs in mid-page.
   useEffect(() => {
      let cancelled = false;
      (async () => {
         if (!token) {
            setErrMsg(
               'No transfer token in the URL — ask the seller to resend the link.'
            );
            setPhase('error');
            return;
         }
         try {
            const res = await verifyTransferHandoffApi(token);
            if (cancelled) return;
            setData(res);
         } catch (err: any) {
            if (cancelled) return;
            setErrMsg(
               err?.response?.data?.error ||
                  'Could not verify this transfer link'
            );
            setPhase('error');
         }
      })();
      return () => {
         cancelled = true;
      };
   }, [token]);

   // React to data + auth-state changes locally — no further server
   // round-trips. Decides which phase the page should be in.
   useEffect(() => {
      if (!data) return; // verify still running, keep 'verifying'
      if (isAuthenticated && user) {
         if (user.email.toLowerCase() === data.toEmail.toLowerCase()) {
            navigate(
               `/wifi-devices?reviewTransfer=${data.transfer._id}`,
               { replace: true }
            );
            return;
         }
         setPhase('mismatched-account');
         return;
      }
      setPhase(
         data.hasExistingAccount ? 'has-account-signin' : 'new-account-signup'
      );
   }, [data, isAuthenticated, user, navigate]);

   /**
    * Google sign-in lands the user in one of two states:
    *   - Google email matches the transfer's toEmail → accept directly,
    *     redirect to /wifi-devices.
    *   - Google email doesn't match → setAuth then flip to mismatched
    *     screen so the buyer can sign out and try again.
    */
   const handleGoogleSuccess = async (result: {
      token: string;
      user: { id?: string; name: string; email: string; verified?: boolean };
   }) => {
      if (!data) return;
      setAuth(result.token, {
         id: result.user.id,
         name: result.user.name,
         email: result.user.email,
         verified: result.user.verified,
      });
      const emailMatches =
         result.user.email.toLowerCase() === data.toEmail.toLowerCase();
      if (!emailMatches) {
         // The useEffect will re-pick this up via isAuthenticated change
         // and route to the mismatched-account screen.
         return;
      }
      // Email matches — accept immediately so the buyer lands on the
      // dashboard with the device already theirs (skipping the Review
      // modal). The freshly-set authStore token is read by apiClient on
      // the next request.
      setAcceptingAfterGoogle(true);
      try {
         await acceptTransferApi(data.transfer._id);
         toast.success('Device accepted — it is now in your dashboard');
         navigate('/wifi-devices', { replace: true });
      } catch (err: any) {
         const code = err?.response?.data?.code;
         // If the transfer is already accepted (race / re-click), just
         // land on devices — the buyer will see the device there.
         if (code === 'already-resolved') {
            navigate('/wifi-devices', { replace: true });
            return;
         }
         toast.error(
            err?.response?.data?.error ||
               'Signed in, but the accept step failed — open Devices and click Review.'
         );
         navigate(
            `/wifi-devices?reviewTransfer=${data.transfer._id}`,
            { replace: true }
         );
      } finally {
         setAcceptingAfterGoogle(false);
      }
   };

   // Live validation: errors surface inline while typing so the user
   // sees the rule failing BEFORE they hit submit. Don't rely on toast —
   // [[feedback_inline_form_validation]] — the toast may not render
   // (no ToastContainer on every route) and toasts dismiss too fast.
   const passwordError = useMemo(() => {
      if (password.length === 0) return '';
      if (password.length < 8) return 'Must be at least 8 characters';
      return '';
   }, [password]);

   const confirmError = useMemo(() => {
      if (confirmPassword.length === 0) return '';
      if (password !== confirmPassword) return "Passwords don't match";
      return '';
   }, [password, confirmPassword]);

   const formValid =
      password.length >= 8 && confirmPassword.length > 0 && password === confirmPassword;

   const handleSignup = async () => {
      if (submitting) return;
      // Cover the case where the user clicks submit without touching
      // fields — surface the rule even with empty input.
      if (password.length < 8 || password !== confirmPassword) {
         // Toast is a supplemental signal; inline errors are primary.
         toast.error(
            password.length < 8
               ? 'Password must be at least 8 characters'
               : "Passwords don't match"
         );
         return;
      }
      setSubmitting(true);
      try {
         const res = await signupFromTransferApi({
            token,
            password,
            name: name.trim() || undefined,
         });
         setAuth(res.token, {
            id: res.user.id,
            email: res.user.email,
            name: res.user.name,
            verified: res.user.verified,
         });
         toast.success('Account created — device added to your dashboard');
         navigate('/wifi-devices', { replace: true });
      } catch (err: any) {
         const code = err?.response?.data?.code;
         const msg = err?.response?.data?.error || 'Sign-up failed';
         if (code === 'already-exists') {
            setPhase('has-account-signin');
         }
         toast.error(msg);
         setSubmitting(false);
      }
   };

   return (
      <Page>
         <AppToasts />
         <Card>
            <Brand>
               <BrandMark>
                  <svg
                     viewBox="0 0 24 24"
                     fill="none"
                     stroke="white"
                     strokeWidth={2}
                     strokeLinecap="round"
                  >
                     <path d="M5 12.55a11 11 0 0 1 14 0" />
                     <path d="M1.42 9a16 16 0 0 1 21.16 0" />
                     <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
                     <circle cx="12" cy="20" r="1.5" fill="white" stroke="none" />
                  </svg>
               </BrandMark>
               <BrandWord>SmartFi</BrandWord>
            </Brand>

            {phase === 'verifying' && (
               <Centered>
                  <Spinner />
                  <Sub>Verifying transfer link…</Sub>
               </Centered>
            )}

            {phase === 'error' && (
               <>
                  <Title>Link can't be opened</Title>
                  <Sub>{errMsg}</Sub>
                  <SecondaryBtn onClick={() => navigate('/')}>
                     Go to SmartFi home
                  </SecondaryBtn>
               </>
            )}

            {phase === 'mismatched-account' && data && (
               <>
                  <Title>Different account needed</Title>
                  <Sub>
                     This transfer was sent to <Email>{data.toEmail}</Email>,
                     but you're signed in as <Email>{user?.email}</Email>.
                     Sign out and sign in with the right email, or ask the
                     seller to re-send the transfer to your current address.
                  </Sub>
                  <TransferSummary data={data} />
                  <ButtonRow>
                     <PrimaryBtn
                        onClick={() => {
                           const next =
                              window.location.pathname +
                              window.location.search;
                           useAuthStore.getState().clearAuth();
                           navigate('/', { state: { from: next } });
                        }}
                     >
                        Sign out & sign in
                     </PrimaryBtn>
                  </ButtonRow>
               </>
            )}

            {phase === 'has-account-signin' && data && (
               <>
                  <Title>Sign in to accept</Title>
                  <Sub>
                     An account already exists for{' '}
                     <Email>{data.toEmail}</Email>. Sign in and the transfer
                     auto-accepts.
                  </Sub>
                  <TransferSummary data={data} />

                  {acceptingAfterGoogle ? (
                     <Centered>
                        <Spinner />
                        <Sub>Accepting transfer…</Sub>
                     </Centered>
                  ) : (
                     <>
                        <GoogleSection>
                           <GoogleAuthButton
                              text="signin_with"
                              onSuccess={handleGoogleSuccess}
                           />
                        </GoogleSection>
                        <OrDivider>
                           <OrLine />
                           <OrText>or use a password</OrText>
                           <OrLine />
                        </OrDivider>
                        <ButtonRow>
                           <PrimaryBtn
                              onClick={() =>
                                 navigate('/', {
                                    state: {
                                       from:
                                          window.location.pathname +
                                          window.location.search,
                                    },
                                 })
                              }
                           >
                              Sign in with password →
                           </PrimaryBtn>
                        </ButtonRow>
                     </>
                  )}
               </>
            )}

            {phase === 'new-account-signup' && data && (
               <>
                  <Title>Create your SmartFi account</Title>
                  <Sub>
                     A seller wants to transfer a vendo device to{' '}
                     <Email>{data.toEmail}</Email>. Fastest way: sign up
                     with Google using the same email — the device lands
                     in your dashboard right after.
                  </Sub>
                  <TransferSummary data={data} />

                  {acceptingAfterGoogle ? (
                     <Centered>
                        <Spinner />
                        <Sub>Accepting transfer…</Sub>
                     </Centered>
                  ) : (
                     <>
                        <GoogleSection>
                           <GoogleAuthButton
                              text="signup_with"
                              onSuccess={handleGoogleSuccess}
                           />
                           <GoogleHint>
                              Use the Google account for{' '}
                              <Email>{data.toEmail}</Email>. A different
                              Google account will work too but won't
                              auto-claim this transfer.
                           </GoogleHint>
                        </GoogleSection>

                        <OrDivider>
                           <OrLine />
                           <OrText>or use a password</OrText>
                           <OrLine />
                        </OrDivider>

                        {!showManualSignup ? (
                           <ManualToggle
                              type="button"
                              onClick={() => setShowManualSignup(true)}
                           >
                              Sign up with email & password →
                           </ManualToggle>
                        ) : (
                           <>
                              <FieldGrid>
                                 <Field>
                                    <FieldLabel>Email</FieldLabel>
                                    <Input
                                       value={data.toEmail}
                                       disabled
                                       readOnly
                                    />
                                 </Field>
                                 <Field>
                                    <FieldLabel>Display name (optional)</FieldLabel>
                                    <Input
                                       placeholder="What should we call you?"
                                       value={name}
                                       onChange={(e) => setName(e.target.value)}
                                    />
                                 </Field>
                                 <Field>
                                    <FieldLabel>Password</FieldLabel>
                                    <Input
                                       type="password"
                                       placeholder="At least 8 characters"
                                       value={password}
                                       onChange={(e) => setPassword(e.target.value)}
                                       $error={!!passwordError}
                                    />
                                    {passwordError ? (
                                       <FieldError>{passwordError}</FieldError>
                                    ) : (
                                       <FieldHint>
                                          8 characters minimum
                                       </FieldHint>
                                    )}
                                 </Field>
                                 <Field>
                                    <FieldLabel>Confirm password</FieldLabel>
                                    <Input
                                       type="password"
                                       placeholder="Type it again"
                                       value={confirmPassword}
                                       onChange={(e) =>
                                          setConfirmPassword(e.target.value)
                                       }
                                       onKeyDown={(e) => {
                                          if (e.key === 'Enter') handleSignup();
                                       }}
                                       $error={!!confirmError}
                                    />
                                    {confirmError && (
                                       <FieldError>{confirmError}</FieldError>
                                    )}
                                 </Field>
                              </FieldGrid>

                              <ButtonRow>
                                 <PrimaryBtn
                                    onClick={handleSignup}
                                    disabled={submitting || !formValid}
                                    title={
                                       !formValid
                                          ? 'Fill in a valid password and confirmation first'
                                          : undefined
                                    }
                                 >
                                    {submitting
                                       ? 'Creating…'
                                       : 'Create account & accept'}
                                 </PrimaryBtn>
                              </ButtonRow>
                           </>
                        )}

                        <SignInHint>
                           Already have an account?{' '}
                           <SignInLink
                              onClick={() =>
                                 navigate('/', {
                                    state: {
                                       from:
                                          window.location.pathname +
                                          window.location.search,
                                    },
                                 })
                              }
                           >
                              Sign in instead
                           </SignInLink>
                        </SignInHint>
                     </>
                  )}
               </>
            )}
         </Card>
      </Page>
   );
};

export default TransferHandoff;

const TransferSummary: React.FC<{ data: TransferHandoffData }> = ({ data }) => {
   const transfer = data.transfer;
   const dev = typeof transfer.device === 'object' ? transfer.device : null;
   const seller =
      typeof transfer.fromOwner === 'object'
         ? transfer.fromOwner.name || transfer.fromOwner.email
         : transfer.fromOwner;
   return (
      <Summary>
         <SummaryRow>
            <SummaryLabel>Device</SummaryLabel>
            <SummaryValue>{dev?.name ?? '—'}</SummaryValue>
         </SummaryRow>
         <SummaryRow>
            <SummaryLabel>From</SummaryLabel>
            <SummaryValue>{seller}</SummaryValue>
         </SummaryRow>
         <SummaryRow>
            <SummaryLabel>Type</SummaryLabel>
            <SummaryValue>
               {transfer.paymentType === 'full-cash'
                  ? `Paid · ₱${(transfer.fullCashAmount ?? 0).toLocaleString()}`
                  : transfer.paymentType === 'installment'
                  ? 'Installment'
                  : 'Plain Transfer'}
            </SummaryValue>
         </SummaryRow>
         <SummaryRow>
            <SummaryLabel>Expires</SummaryLabel>
            <SummaryValue>
               {new Date(transfer.expiresAt).toLocaleDateString()}
            </SummaryValue>
         </SummaryRow>
         {transfer.note && (
            <SummaryRow>
               <SummaryLabel>Note</SummaryLabel>
               <SummaryValue>"{transfer.note}"</SummaryValue>
            </SummaryRow>
         )}
      </Summary>
   );
};

const Page = styled.div`
   min-height: 100vh;
   background: linear-gradient(160deg, #0b1030 0%, #1a1f4a 40%, #2a2165 100%);
   display: flex;
   align-items: center;
   justify-content: center;
   padding: 24px 16px;
`;

const Card = styled.div`
   background: ${colors.WHITE};
   border-radius: 14px;
   padding: 32px;
   width: 100%;
   max-width: 720px;
   box-shadow: 0 24px 60px rgba(0, 0, 0, 0.35);
`;

const Brand = styled.div`
   display: flex;
   align-items: center;
   gap: 8px;
   margin-bottom: 18px;
`;

/* Same WiFi-signal mark + gradient blue badge as SideNav.LogoBadge —
 * keep these visually identical so the handoff card reads as SmartFi
 * the moment the buyer lands on it. */
const BrandMark = styled.span`
   display: inline-grid;
   place-items: center;
   width: 34px;
   height: 34px;
   border-radius: 10px;
   background: linear-gradient(135deg, #3760f9 0%, #6a4dff 100%);
   box-shadow: 0 4px 14px rgba(55, 96, 249, 0.45);
   flex-shrink: 0;
   svg {
      width: 20px;
      height: 20px;
   }
`;

const BrandWord = styled.span`
   font-size: 18px;
   font-weight: 800;
   color: ${colors.GRAY_DARK};
`;

const Title = styled.h1`
   margin: 0 0 8px;
   font-size: 22px;
   font-weight: 800;
   color: ${colors.GRAY_DARK};
`;

const Sub = styled.p`
   margin: 0 0 18px;
   font-size: 13px;
   line-height: 1.6;
   color: ${colors.GRAY3};
`;

const Email = styled.strong`
   color: ${colors.GRAY_DARK};
   font-weight: 700;
`;

const Summary = styled.div`
   display: flex;
   flex-direction: column;
   gap: 6px;
   padding: 14px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 10px;
   margin-bottom: 18px;
   background: ${colors.GRAY14};
`;

const SummaryRow = styled.div`
   display: grid;
   grid-template-columns: 80px 1fr;
   gap: 8px;
   font-size: 12px;
`;

const SummaryLabel = styled.div`
   color: ${colors.GRAY5};
   font-weight: 700;
   text-transform: uppercase;
   letter-spacing: 0.04em;
   font-size: 10px;
   align-self: center;
`;

const SummaryValue = styled.div`
   color: ${colors.GRAY_DARK};
   font-weight: 500;
`;

const FieldStack = styled.div`
   display: flex;
   flex-direction: column;
   gap: 6px;
   margin-bottom: 16px;
`;

/* 2-col grid for the password-signup branch — keeps the card from
 * stretching vertically on short laptops. Collapses to single column
 * <640px so phones / narrow viewports still read top-down. */
const FieldGrid = styled.div`
   display: grid;
   grid-template-columns: 1fr 1fr;
   gap: 10px 16px;
   margin-bottom: 16px;
   @media (max-width: 640px) {
      grid-template-columns: 1fr;
   }
`;

const Field = styled.div`
   display: flex;
   flex-direction: column;
   gap: 4px;
`;

const FieldLabel = styled.label`
   font-size: 11px;
   font-weight: 700;
   color: ${colors.GRAY3};
   text-transform: uppercase;
   letter-spacing: 0.04em;
   margin-top: 6px;
`;

const Input = styled.input<{ $error?: boolean }>`
   padding: 10px 12px;
   border-radius: 8px;
   border: 1px solid
      ${({ $error }) => ($error ? colors.DANGER : colors.GRAY11)};
   font-size: 13px;
   color: ${colors.GRAY_DARK};
   background: ${colors.WHITE};
   &:focus {
      outline: none;
      border-color: ${({ $error }) => ($error ? colors.DANGER : colors.PRIMARY)};
   }
   &:disabled {
      background: ${colors.GRAY14};
      color: ${colors.GRAY5};
   }
`;

const FieldError = styled.div`
   font-size: 11px;
   color: ${colors.DANGER};
   font-weight: 600;
   margin-top: 2px;
`;

const FieldHint = styled.div`
   font-size: 11px;
   color: ${colors.GRAY5};
   margin-top: 2px;
`;

const ButtonRow = styled.div`
   display: flex;
   justify-content: flex-end;
   gap: 8px;
`;

const PrimaryBtn = styled.button`
   background: ${colors.PRIMARY};
   color: ${colors.WHITE};
   border: none;
   border-radius: 8px;
   padding: 12px 22px;
   font-size: 13px;
   font-weight: 700;
   cursor: pointer;
   min-width: 200px;
   &:hover {
      opacity: 0.9;
   }
   &:disabled {
      background: ${colors.GRAY10};
      cursor: not-allowed;
   }
`;

const SecondaryBtn = styled.button`
   background: transparent;
   color: ${colors.PRIMARY};
   border: 1px solid ${colors.PRIMARY}40;
   border-radius: 8px;
   padding: 10px 18px;
   font-size: 12px;
   font-weight: 700;
   cursor: pointer;
   &:hover {
      background: ${colors.PRIMARY}10;
   }
`;

const GoogleSection = styled.div`
   margin-bottom: 8px;
`;

const GoogleHint = styled.p`
   margin: 6px 2px 0;
   font-size: 11px;
   color: ${colors.GRAY5};
   line-height: 1.5;
`;

const OrDivider = styled.div`
   display: flex;
   align-items: center;
   gap: 10px;
   margin: 16px 0;
`;

const OrLine = styled.div`
   flex: 1;
   height: 1px;
   background: ${colors.GRAY12};
`;

const OrText = styled.div`
   font-size: 11px;
   font-weight: 700;
   color: ${colors.GRAY5};
   text-transform: uppercase;
   letter-spacing: 0.06em;
`;

const ManualToggle = styled.button`
   width: 100%;
   background: transparent;
   color: ${colors.PRIMARY};
   border: 1px solid ${colors.PRIMARY}40;
   border-radius: 8px;
   padding: 10px 16px;
   font-size: 12px;
   font-weight: 700;
   cursor: pointer;
   &:hover {
      background: ${colors.PRIMARY}10;
   }
`;

const SignInHint = styled.div`
   margin-top: 14px;
   font-size: 12px;
   color: ${colors.GRAY5};
   text-align: center;
`;

const SignInLink = styled.button`
   background: none;
   border: none;
   padding: 0;
   font: inherit;
   color: ${colors.PRIMARY};
   font-weight: 700;
   cursor: pointer;
   text-decoration: underline;
`;

const Centered = styled.div`
   display: flex;
   flex-direction: column;
   align-items: center;
   gap: 12px;
   padding: 32px 0;
`;

const Spinner = styled.div`
   width: 36px;
   height: 36px;
   border-radius: 50%;
   border: 3px solid ${colors.GRAY12};
   border-top-color: ${colors.PRIMARY};
   animation: spin 0.8s linear infinite;
   @keyframes spin {
      to {
         transform: rotate(360deg);
      }
   }
`;
